/**
 * ChannelsPage — Patch 33.2.
 * Customer-facing publishing-channel management. Provider-neutral copy
 * (never uses the word "Zernio"). Accessible + mobile-first.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, Plug, Trash2, Unplug } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { toast } from "sonner";

import {
  LAUNCH_PLATFORMS,
  PublishingChannelsAPI,
  type ChannelsResponse,
  type ConnectedChannel,
  type LaunchPlatformKey,
} from "@/entities/channels";
import { useCurrentWorkspace } from "@/entities/workspace";

const CANONICAL_STATUS_COPY: Record<string, { label: string; tone: "success" | "warn" | "danger" | "muted"; hint: string }> = {
  connected: { label: "Connected", tone: "success", hint: "Ready to publish" },
  syncing: { label: "Syncing", tone: "muted", hint: "Finishing account setup" },
  disconnected: { label: "Disconnected", tone: "danger", hint: "Publishing unavailable — reconnect to resume" },
  revoked: { label: "Needs Attention", tone: "warn", hint: "The account revoked access — reconnect to restore" },
  error: { label: "Needs Attention", tone: "warn", hint: "Reconnect this account to continue publishing" },
  over_limit: { label: "Locked", tone: "muted", hint: "Your plan no longer includes this connection" },
};

function statusCopy(status: string) {
  return CANONICAL_STATUS_COPY[status] ?? CANONICAL_STATUS_COPY.error;
}

function toneBadgeClass(tone: "success" | "warn" | "danger" | "muted"): string {
  switch (tone) {
    case "success":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
    case "warn":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30";
    case "danger":
      return "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30";
    case "muted":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

export default function ChannelsPage() {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery<ChannelsResponse>({
    queryKey: ["publishing", "channels", workspaceId],
    queryFn: () => PublishingChannelsAPI.listChannels(workspaceId!),
    enabled: !!workspaceId,
  });

  const syncMutation = useMutation({
    mutationFn: () => PublishingChannelsAPI.syncChannels(workspaceId!),
    onSuccess: (r) => {
      toast.success(`Synchronised ${r.synced_count} account${r.synced_count === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["publishing", "channels", workspaceId] });
    },
    onError: () => toast.error("Sync failed. Please try again."),
  });

  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => PublishingChannelsAPI.disconnect(workspaceId!, connectionId),
    onSuccess: () => {
      toast.success("Channel disconnected");
      qc.invalidateQueries({ queryKey: ["publishing", "channels", workspaceId] });
    },
    onError: () => toast.error("Disconnect failed. Please try again."),
  });

  const connectMutation = useMutation({
    mutationFn: async (platform: LaunchPlatformKey) => {
      const redirect = `${window.location.origin}/channels?state=return`;
      return PublishingChannelsAPI.startConnect(workspaceId!, platform, redirect);
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.reason ?? "Unable to start connection");
        return;
      }
      if (r.url) {
        // Full-page redirect degrades safely on mobile (per exec directive Part XIV).
        window.location.assign(r.url);
      }
    },
    onError: () => toast.error("Unable to start OAuth flow"),
  });

  const connectedByPlatform = useMemo(() => {
    const map: Record<string, ConnectedChannel[]> = {};
    for (const c of data?.channels ?? []) {
      (map[c.platform] ??= []).push(c);
    }
    return map;
  }, [data]);

  if (!workspaceId) {
    return (
      <div className="p-6" data-testid="channels-page-no-workspace">
        <p className="text-muted-foreground">Select a workspace to manage publishing channels.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6" data-testid="channels-page">
      {/* Header */}
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="channels-title">
          Publishing Channels
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Connect the social accounts Orkexra uses to publish. Every account counts against your plan&apos;s
          publishing-channel allowance.
        </p>
      </header>

      {/* Entitlement banner */}
      <Card data-testid="channels-entitlement-banner">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          {isLoading ? (
            <Skeleton className="h-8 w-56" />
          ) : (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Channel usage
              </p>
              <p className="mt-1 text-lg font-medium" data-testid="channels-usage">
                {data?.limit.used ?? 0}{" "}
                <span className="text-muted-foreground">
                  of {data?.limit.limit ?? "∞"} Publishing Channels connected
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{data?.limit.reason}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              data-testid="channels-sync-button"
            >
              {syncMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Sync now
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled
              data-testid="channels-add-more"
              title="Additional channel packs will be available soon"
            >
              Add more channels
            </Button>
          </div>
        </CardContent>
      </Card>

      {isError && (
        <Card className="border-rose-500/40 bg-rose-500/5" data-testid="channels-error">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 text-rose-600" />
            <div className="text-sm">
              <p className="font-medium text-rose-700 dark:text-rose-300">
                Unable to load your publishing channels.
              </p>
              <p className="text-xs text-muted-foreground">
                {(error as Error)?.message ?? "Please refresh and try again."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Launch platforms */}
      <section className="space-y-3" aria-labelledby="platforms-heading">
        <h2 id="platforms-heading" className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Available social platforms
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {LAUNCH_PLATFORMS.map((p) => {
            const existing = connectedByPlatform[p.key] ?? [];
            return (
              <Card key={p.key} data-testid={`platform-card-${p.key}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>{p.label}</span>
                    {existing.length > 0 ? (
                      <Badge variant="outline" className={toneBadgeClass("success")}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {existing.length}
                      </Badge>
                    ) : null}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 pb-4">
                  {existing.map((c) => {
                    const copy = statusCopy(c.status);
                    return (
                      <div
                        key={c.id}
                        className="flex items-start justify-between gap-2 rounded-md border p-2 text-sm"
                        data-testid={`channel-card-${c.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{c.display_name ?? c.username ?? c.provider_account_id}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <Badge variant="outline" className={toneBadgeClass(copy.tone)}>
                              {copy.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{copy.hint}</span>
                          </div>
                          {c.external_url ? (
                            <a
                              href={c.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              View profile <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : null}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => disconnectMutation.mutate(c.id)}
                          disabled={disconnectMutation.isPending}
                          aria-label={`Disconnect ${c.display_name ?? c.platform}`}
                          data-testid={`channel-disconnect-${c.id}`}
                        >
                          {disconnectMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => connectMutation.mutate(p.key)}
                    disabled={
                      connectMutation.isPending || (data?.limit && data.limit.allowed === false)
                    }
                    data-testid={`connect-${p.key}-btn`}
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plug className="mr-2 h-4 w-4" />
                    )}
                    {existing.length > 0 ? "Connect another account" : `Connect ${p.label}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {data?.channels.length === 0 && !isLoading ? (
        <Card className="border-dashed" data-testid="channels-empty">
          <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
            <Unplug className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">No channels connected yet</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Connect at least one social account to start publishing from Orkexra. Your team can review, approve
              and schedule content once accounts are ready.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
